import { useRef, useCallback } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'

const DIGIT_COUNT = 6

const OtpInput = ({ digits, onChange, disabled = false }) => {
  const inputRefs = useRef([])

  const focusAt = useCallback(index => {
    const el = inputRefs.current[index]
    if (el) el.focus()
  }, [])

  const refCallback = useCallback(
    i => el => {
      inputRefs.current[i] = el
    },
    []
  )

  const handleChange = useCallback(
    (e, index) => {
      const raw = e.target.value.replace(/\D/g, '')
      if (!raw) return

      const digit = raw.slice(-1)
      const next = digits.split('')
      next[index] = digit
      onChange(next.join('').slice(0, DIGIT_COUNT))

      requestAnimationFrame(() => focusAt(Math.min(index + 1, DIGIT_COUNT - 1)), 0)
    },
    [digits, focusAt, onChange]
  )

  const handleKeyDown = useCallback(
    (e, index) => {
      if (e.key === 'Backspace') {
        e.preventDefault()
        if (digits[index]) {
          const next = digits.split('')
          next[index] = ''
          onChange(next.join(''))
        } else if (index > 0) {
          const next = digits.split('')
          next[index - 1] = ''
          onChange(next.join(''))
          focusAt(index - 1)
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusAt(index - 1)
      } else if (e.key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
        focusAt(index + 1)
      }
    },
    [digits, focusAt, onChange]
  )

  const handlePaste = useCallback(
    e => {
      e.preventDefault()
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGIT_COUNT)
      if (pasted) {
        onChange(pasted)
        requestAnimationFrame(() => focusAt(Math.min(pasted.length, DIGIT_COUNT - 1)), 0)
      }
    },
    [focusAt, onChange]
  )

  return (
    <div className="otp-input flex justify-center gap-2 max-phone:gap-1">
      {Array.from({ length: DIGIT_COUNT }, (_, i) => (
        <input
          key={i}
          ref={refCallback(i)}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] ?? ''}
          onChange={e => handleChange(e, i)}
          onKeyDown={e => handleKeyDown(e, i)}
          onPaste={handlePaste}
          disabled={disabled}
          className={clsx(
            'otp-input__box font-avenir text-xl font-semibold text-grey-10 border border-grey-50 bg-white transition-colors duration-150 w-12 h-9 p-1.5 text-center rounded outline-none focus:border-input-focus disabled:bg-grey-98 disabled:text-grey-70 disabled:cursor-not-allowed max-phone:w-10',
            { 'otp-input__box--filled': digits[i] }
          )}
          autoComplete="one-time-code"
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  )
}

OtpInput.propTypes = {
  digits: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default OtpInput
